import React, { useState, useContext } from "react";
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Card, Avatar, Box, CircularProgress } from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import { makeStyles } from "@material-ui/core/styles";


// context and APIs
import NotificationContext from "views/Components/Context.js";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

const useStyles = makeStyles((theme) => ({
    ...styles,
    card: {
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(1),
        display: "flex",
        alignItems: "center",
    },
    avatar: {
        marginRight: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
    },
    email: {
        color: theme.palette.text.secondary,
    },
    nameContainer: {
        display: "flex",
        flexDirection: "column",
    },
    role: {
        marginLeft: "auto",
        fontWeight: 500,
        textTransform: "capitalize",
        color: theme.palette.text.secondary,
    },
}));


const InviteCollaboratorsModal = ({ open, handleClose, inviteCollaboratorsSelectedAsset, setInviteCollaboratorsSelectedAsset, handleInviteSend }) => {
    // Context and state variables
    const { showNotification } = useContext(NotificationContext);
    const classes = useStyles();

    const [newEmail, setNewEmail] = useState("");
    const [loading, setLoading] = useState({ loading: false, message: "" });

    const _handleInviteSend = async () => {
        if (!_validateEmail()) return;

        setLoading({ loading: true, message: "Sending invite..." });
        const newInvitee = {
            _id: inviteCollaboratorsSelectedAsset._id,
            email: newEmail.trim(),
        };

        setInviteCollaboratorsSelectedAsset(newInvitee)
        try {
            await handleInviteSend(newInvitee);
            setNewEmail("");
        }
        catch (error) {
            console.error("Error sending invite:", error);
            showNotification("Failed to send invite. Please try again.", "danger");
        }
        finally {
            setLoading({ loading: false, message: "" });
        }


    };

    const stringToColor = (string) => {
        let hash = 0;
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const color = `hsl(${hash % 360}, 70%, 60%)`; // pastel-ish HSL
        return color;
    };

    const _validateEmail = () => {
        const trimmedEmail = newEmail.trim();

        // Check empty input
        if (!trimmedEmail) {
            showNotification("Please enter an email address", "danger");
            return false;
        }

        // Basic email regex for validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            showNotification("Please enter a valid email address", "danger");
            return false;
        }

        // Check for duplicates in invitees, allow reinvite only if status is 'rejected'
        const invitees = inviteCollaboratorsSelectedAsset?.invitees || [];
        const existing = invitees.find(i => i.email.toLowerCase() === trimmedEmail.toLowerCase());

        if (existing) {
            const status = existing.status?.toLowerCase() || "pending";
            if (status !== "rejected") {
                showNotification(`This email has already been invited.`, "warning");
                return false;
            }
            // If status is "rejected", allow reinvite (optional: remove old entry here if needed)
            showNotification(`This email was previously rejected. Sending invitation again.`, "info");
        }

        return true;
    };


    const _handleClose = () => {
        setNewEmail("");
        setLoading({ loading: false, message: "" });
        handleClose();
    }

    return (
        <Dialog open={open} onClose={_handleClose} fullWidth maxWidth="md">
            <DialogTitle>Invite Collaborators</DialogTitle>
            <DialogContent>
                {loading.loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="60vh"
                    >
                        <CircularProgress color="quCreateColor" className={classes.loaderClass} />
                        <Typography variant="h6" style={{ marginLeft: 16, textTransform: 'none' }}>
                            {loading.message}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body1">
                            Collaborators will have access to edit and review the project's content.
                        </Typography>
                        <TextField
                            autoFocus
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            margin="dense"
                            id="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                        />

                        <Typography style={{ marginTop: 20 }}>People with edit access</Typography>

                        <div style={{ display: "flex", flexDirection: "column", marginTop: 10 }}>
                            {inviteCollaboratorsSelectedAsset?.invitees?.length > 0 ? (
                                inviteCollaboratorsSelectedAsset.invitees.map((collaborator, index) => {
                                    const nameOrEmail = collaborator.name || collaborator.email;
                                    const firstChar = nameOrEmail?.charAt(0).toUpperCase();
                                    const bgColor = stringToColor(nameOrEmail);

                                    return (
                                        <Card key={index} className={classes.card} variant="outlined">
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item>
                                                    <Avatar
                                                        style={{ backgroundColor: bgColor }}
                                                        className={classes.avatar}
                                                    >
                                                        {firstChar}
                                                    </Avatar>
                                                </Grid>
                                                <Grid item xs>
                                                    <div className={classes.nameContainer}>
                                                        {collaborator.invitee_name && (
                                                            <Typography variant="body1">{collaborator.invitee_name}</Typography>
                                                        )}
                                                        <Typography variant="body2" className={classes.email}>
                                                            {collaborator.email}
                                                        </Typography>
                                                    </div>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="body2" className={classes.role}>
                                                        {collaborator.status}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    );
                                })
                            ) : (
                                <Typography variant="body2" style={{ marginTop: 8, color: "#888" }}>
                                    No collaborators have been invited yet.
                                </Typography>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="quSandboxColor">
                    Cancel
                </Button>
                <Button onClick={_handleInviteSend} color="quCreateColor">
                    Send Invite
                </Button>
            </DialogActions>
        </Dialog >
    );
}

export default InviteCollaboratorsModal;